import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Avatar } from '@/components/brand/Brand';
import { Button } from '@/components/ui/Button';
import { CourseThumb } from '@/components/ui/CourseTexture';
import { EmptyState, Skeleton } from '@/components/ui/Feedback';
import { Pill, PillGroup } from '@/components/ui/Pill';
import { useCourseIndex } from '@/lib/api/queries';
import { useCurrentUser } from '@/lib/auth/AuthContext';
import { fromCoordinates, haversineMeters } from '@/lib/geo/geoPoint';
import { fmtCourseDistance } from '@/lib/geo/units';
import { useGeolocation } from '@/lib/geo/useGeolocation';
import { routes } from '@/routes/paths';
import { collator, t } from '@/lib/i18n';
import styles from './Courses.module.css';
import { CheckIcon, SearchIcon } from '@/components/icons';

type Filter = 'all' | 'verified' | '18' | '9';

export function CoursesList() {
  const user = useCurrentUser();
  const index = useCourseIndex();
  const geo = useGeolocation();

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const courses = useMemo(() => [...(index.data?.values() ?? [])], [index.data]);

  /**
   * Search, filtering and distance all happen here: there is no text-search endpoint, no
   * `verified` filter on `GET /courses`, and `GET /courses/nearby` sorts by distance without
   * returning one. The course list is small and cached, so resolving locally is both simpler
   * and faster than three round trips.
   */
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();

    return courses
      .filter((c) => {
        if (q && !`${c.name} ${c.city ?? ''}`.toLowerCase().includes(q)) return false;
        if (filter === 'verified' && !c.verified) return false;
        if (filter === '18' && c.totalHoles !== 18) return false;
        if (filter === '9' && c.totalHoles !== 9) return false;
        return true;
      })
      .map((course) => ({
        course,
        distance:
          geo.status === 'granted'
            ? haversineMeters(geo.position, fromCoordinates(course.location.coordinates))
            : null,
      }))
      .sort((a, b) => {
        // The home course always leads — it is the one the player opens most.
        if (a.course.id === user.homeCourseId) return -1;
        if (b.course.id === user.homeCourseId) return 1;
        if (a.distance != null && b.distance != null) return a.distance - b.distance;
        return collator.compare(a.course.name, b.course.name);
      });
  }, [courses, query, filter, geo, user.homeCourseId]);

  if (index.isLoading) return <ListSkeleton name={user.displayName} />;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerBar}>
          <h1 className={styles.title}>{t('nav.courses')}</h1>
          <Link to={routes.profile} className={styles.avatarSlot} aria-label={t('nav.aria.profile')}>
            <Avatar name={user.displayName} size={34} />
          </Link>
        </div>

        <div className={styles.search}>
          <SearchIcon size={18} className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder={t('courses.searchPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={t('courses.searchAria')}
          />
        </div>

        <PillGroup scroll className={styles.filters} label={t('courses.filtersAria')}>
          <Pill tone="onGreen" selected={filter === 'all'} onClick={() => setFilter('all')}>
            {t('courses.filter.all')}
          </Pill>
          <Pill tone="onGreen" selected={filter === '18'} onClick={() => setFilter('18')}>
            {t('courses.filter.18')}
          </Pill>
          <Pill tone="onGreen" selected={filter === '9'} onClick={() => setFilter('9')}>
            {t('courses.filter.9')}
          </Pill>
          <Pill
            tone="onGreen"
            selected={filter === 'verified'}
            onClick={() => setFilter('verified')}
          >
            {t('pill.verified')}
          </Pill>
        </PillGroup>
      </header>

      <div className={styles.strip}>
        <span>
          {geo.status === 'granted' ? t('courses.sortedByDistance') : t('courses.sortedByName')}
        </span>
        {geo.status !== 'granted' && (
          <button
            type="button"
            className={styles.stripAction}
            onClick={geo.request}
            disabled={geo.status === 'asking'}
          >
            {geo.status === 'asking' ? t('courses.locating') : t('courses.useMyLocation')}
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title={t('courses.empty.title')}
          actions={
            query ? (
              <Button variant="secondary" block onClick={() => setQuery('')}>
                {t('courses.empty.clearSearch')}
              </Button>
            ) : undefined
          }
        >
          {query ? t('courses.empty.noMatch', { query }) : t('courses.empty.none')}
        </EmptyState>
      ) : (
        rows.map(({ course, distance }) => (
          <Link key={course.id} to={routes.course(course.id)} className={styles.row}>
            <CourseThumb location={course.location} size={52} />
            <span className={styles.rowBody}>
              <span className={styles.rowName}>
                {course.name}
                {course.verified && (
                  <span
                    className={styles.verifiedTick}
                    title={t('pill.verifiedTitle')}
                    aria-label={t('courses.verifiedTick')}
                  >
                    <CheckIcon size={13} />
                  </span>
                )}
              </span>
              <span className={styles.rowCity}>{course.city ?? '—'}</span>
              <span className={styles.rowSpec}>
                {t('courses.spec', { holes: course.totalHoles, par: course.parTotal })}
              </span>
            </span>
            <span className={styles.rowRight}>
              {distance != null && (
                <span className={styles.rowDistance}>{fmtCourseDistance(distance)}</span>
              )}
              {course.id === user.homeCourseId && (
                <span className={styles.rowTag}>{t('courses.homeTag')}</span>
              )}
            </span>
          </Link>
        ))
      )}

      {rows.length > 0 && (
        <p className={styles.footerNote}>{t('courses.count', { count: rows.length })}</p>
      )}
    </div>
  );
}

function ListSkeleton({ name }: { name: string }) {
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerBar}>
          <h1 className={styles.title}>{t('nav.courses')}</h1>
          <Avatar name={name} size={34} />
        </div>
      </header>
      {[0, 1, 2].map((i) => (
        <div key={i} className={styles.row}>
          <Skeleton width={52} height={52} radius={10} />
          <span className={styles.rowBody}>
            <Skeleton width="55%" height={14} />
            <Skeleton width="35%" height={10} style={{ marginTop: 8 }} />
          </span>
        </div>
      ))}
    </div>
  );
}
